import { TabViewModule } from 'primeng/tabview';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { SharedComponentsModule } from '../../../shared/shared-components/shared-components.module';
import { MessageService } from 'primeng/api';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ReportsService } from '../../service/reports.service';
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { Region } from '../promotions/promotions.component';
import { DropdownModule } from 'primeng/dropdown';
import { PromotionsService, Voucher, Winner } from '../../service/promotions.service';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';

export interface PromotionsListRequest {
    dateFrom: string;
    dateTo: string;
    status: string;
    region: string;
    promotionId: number;
}

export interface PromotionsPrice {
    label: string;
    priceAmount: number;
}
@Component({
    selector: 'app-spinning-wheel',
    standalone: true,
    imports: [
        CommonModule,
        CalendarModule,
        ButtonModule,
        FormsModule,
        InputTextModule,
        InputTextareaModule,
        TabViewModule,
        ReactiveFormsModule,
        DialogModule,
        DropdownModule,
        ToastModule,
        CardModule,
        SharedComponentsModule,
        ProgressSpinnerModule
    ],
    templateUrl: './spinning-wheel.component.html',
    styleUrl: './spinning-wheel.component.scss',
    providers: [MessageService],
})
export class SpinningWheelComponent {
    @ViewChild('wheel') wheel!: ElementRef<HTMLCanvasElement>;
    @ViewChild('pointer') pointer!: ElementRef<HTMLDivElement>;

    @Input()
    promoVehicles: any[] = [];

    @Input()
    regions: Region[] = [];

    @Input()
    loadingItems: boolean = false;

    @Output()
    loadPromoItemsClicked = new EventEmitter<PromotionsListRequest>()

    @Output()
    winnerSaveEvent = new EventEmitter<any>();

    promoForm!: FormGroup;
    createPromoForm!: FormGroup;
    createVoucherForm!: FormGroup;
    loadingVehicles!: boolean;
    vehicles!: any[];

    createPromoVouchersList: any[] = [];

    messageService = inject(MessageService);
    promotionsList = [];

    vouchers: PromotionsPrice[] = [
        { label: 'USD20 Voucher', priceAmount: 20 },
        { label: 'USD30 Voucher', priceAmount: 30 },
        { label: 'USD50 Voucher', priceAmount: 50 },
    ]

    fb = inject(FormBuilder);
    reportsService = inject(ReportsService);
    promotionsService = inject(PromotionsService);

    segments: any[] = [
        { id: 1, text: 'Champions', color: '#FF5252', textColor: '#FFF' },
        { id: 2, text: 'Insurance', color: '#FFEB3B', textColor: '#333' },
        { id: 3, text: 'Champions', color: '#4CAF50', textColor: '#FFF' },
        { id: 4, text: 'Insurance', color: '#2196F3', textColor: '#FFF' },
        { id: 5, text: 'Champions', color: '#9C27B0', textColor: '#FFF' },
        { id: 6, text: 'Insurance', color: '#FF9800', textColor: '#333' },
        { id: 7, text: 'Champions', color: '#607D8B', textColor: '#FFF' },
        { id: 8, text: 'Insurance', color: '#00BCD4', textColor: '#FFF' },
    ];

    spinning = false;
    promoWinner: any = null;
    displayPromoWinner: any = null;

    currentRotation = 0;
    result: any = null;
    prize: PromotionsPrice | null = null;
    confettiActive = false;

    createdPromotionId: number | null = null;
    savingPromotion = false;
    savingVoucher = false;

    runningPromotion = false;
    promotionVouchers: Voucher[] = [];

    ngOnInit() {
        this.initForm();
        this.initCreatePromoForm();
        this.initializeVoucherForm();
    }

    initForm() {
        this.promoForm = this.fb.group({
            startDate: ['', Validators.required],
            endDate: ['', Validators.required],
            region: ['', Validators.required],
            promotionId: ['', Validators.required]
        });
    }

    initCreatePromoForm() {
        this.createPromoForm = this.fb.group({
            name: ['', Validators.required],
            startDate: ['', Validators.required],
            endDate: ['', Validators.required]
        })
    }

    initializeVoucherForm() {
        this.createVoucherForm = this.fb.group({
            name: ['', Validators.required],
            amount: ['', [Validators.required, Validators.min(1)]],
            weight: ['', [Validators.required, Validators.min(1)]]
        })
    }

    ngAfterViewInit() {
        this.fetchAvailablePromotions()
        this.drawWheel();
        gsap.registerPlugin(CustomEase);
        CustomEase.create(
            'wheelEase',
            'M0,0 C0.1,0 0.24,1 0.38,1 0.52,1 0.7,0 1,0'
        );
        gsap.set(this.wheel.nativeElement, { rotation: 0 });
    }

    formatDate(inputDate: string): string {
        const date = new Date(inputDate);
        return (
            date.getFullYear() + "-" +
            String(date.getMonth() + 1).padStart(2, "0") + "-" +
            String(date.getDate()).padStart(2, "0") + "T" +
            String(date.getHours()).padStart(2, "0") + ":" +
            String(date.getMinutes()).padStart(2, "0"));
    }

    formatCreatePromoDate(inputDate: string): string {
        const date = new Date(inputDate);
        return (date.getFullYear() + "-" +
            String(date.getMonth() + 1).padStart(2, "0") + "-" +
            String(date.getDate()).padStart(2, "0"));
    }

    drawWheel() {
        const canvas = this.wheel.nativeElement;
        const ctx = canvas.getContext('2d')!;
        const center = canvas.width / 2;
        const radius = center - 8;
        const segmentAngle = (2 * Math.PI) / this.segments.length;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw segments
        this.segments.forEach((segment, i) => {
            ctx.beginPath();
            ctx.moveTo(center, center);
            ctx.arc(
                center,
                center,
                radius,
                i * segmentAngle,
                (i + 1) * segmentAngle
            );
            ctx.closePath();
            ctx.fillStyle = segment.color;
            ctx.fill();

            // Draw segment border
            ctx.strokeStyle = 'rgba(255,255,255,0.3)';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Draw text
            ctx.save();
            ctx.translate(center, center);
            ctx.rotate(i * segmentAngle + segmentAngle / 2);
            ctx.textAlign = 'right';
            ctx.fillStyle = segment.textColor;
            ctx.font = 'bold 14px Arial';
            ctx.fillText(segment.text, radius - 20, 5);
            ctx.restore();
        });

        // Draw center circle
        ctx.beginPath();
        ctx.arc(center, center, 30, 0, 2 * Math.PI);
        ctx.fillStyle = '#FFF';
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.1)';
        ctx.lineWidth = 5;
        ctx.stroke();
    }

    fetchVehicles() {
        this.spin()
        const returnItem = {
            dateFrom: this.formatDate(this.promoForm.get('startDate').value),
            dateTo: this.formatDate(this.promoForm.get('endDate').value),
            status: 'APPROVED',
            region: this.promoForm.get('region').value,
            promotionId: parseInt(this.promoForm.get('promotionId').value),
        }

        this.runningPromotion = true;
        this.promotionsService.fetchPromoItems(returnItem.dateFrom.toString() + ':00.000Z', returnItem.dateTo.toString() + ':00.000Z', returnItem.status, returnItem.region, returnItem.promotionId).subscribe({

            next: value => {
                this.runningPromotion = false;
                this.promoWinner = value;
                this.displayPromoWinner = value;
            }
            , error: err => {
                this.runningPromotion = false;
                this.showError('You no longer have customers under your selected region');
            }
        });
    }

    onTabChange(event: any) {
        console.log('Index:', event.index);
        this.fetchAvailablePromotions();
    }

    fetchAvailablePromotions() {
        this.promotionsService.fetchAllPromotions().subscribe({
            next: value => {
                console.log("Fetched promotions: ", value)
                this.promotionsList = value['promotions']
            },
            error: err => {
                this.showError(err.error.message)
            }
        })
    }

    spin() {
        if (this.spinning) return;

        this.spinning = true;

        const segmentAngle = 360 / this.segments.length;
        const winningIndex = Math.floor(Math.random() * this.segments.length);

        const targetRotation =
            360 - segmentAngle * winningIndex - segmentAngle / 2;

        const extraSpins = 360 * 10;
        const finalRotation = this.currentRotation + extraSpins + targetRotation;

        gsap.to(this.wheel.nativeElement, {
            rotate: finalRotation,
            duration: 3,
            ease: 'power2.out',
            onComplete: () => {
                this.spinning = false;
                this.currentRotation = finalRotation % 360;
                this.resetWheel();
            },
        });
    }

    resetWheel() {
        //this.displayPromoWinner = this.promoWinner;
        gsap.set(this.wheel.nativeElement, { rotate: 0 });
        this.currentRotation = 0;
    }

    createNewPromotion() {
        const payload = {
            name: this.createPromoForm.get('name').value,
            startDate: this.formatCreatePromoDate(this.createPromoForm.get('startDate').value),
            endDate: this.formatCreatePromoDate(this.createPromoForm.get('endDate').value),
            vouchers: this.promotionVouchers
        };
        this.savingPromotion = true;
        this.promotionsService.createNewPromotion(payload).subscribe({
            next: value => {
                this.savingPromotion = false;
                this.showSuccess("Promotion created successfully");
                this.createdPromotionId = value["promotion"].id;
            },
            error: err => {
                this.savingPromotion = false;
                this.showError(err.error.message)
            }
        })
    }

    showSuccess(message: string) {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: message });
    }


    showError(message: string) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
    }

    createNewPromotionVoucher() {
        const payload = {
            name: this.createVoucherForm.get('name').value,
            amount: this.createVoucherForm.get('amount').value,
            weight: this.createVoucherForm.get('weight').value,
        };

        this.promotionVouchers.push(payload);
        this.showSuccess("Voucher has been added to promotion");
        this.createVoucherForm.reset();
    }


    getCurrentSegment(): number {
        const segmentAngle = 360 / this.segments.length;
        // Normalize rotation to positive degrees (0-360)
        const normalizedRotation = (360 - (this.currentRotation % 360)) % 360;
        // Calculate which segment is under the pointer (adding 90° because pointer is at top)
        const pointerAngle = (normalizedRotation + 90) % 360;
        return Math.floor(pointerAngle / segmentAngle) % this.segments.length;
    }

    triggerConfetti() {
        this.confettiActive = true;
        setTimeout(() => (this.confettiActive = false), 3000);
    }

    reset() {
        this.promoVehicles = [];
        this.result = null;
        this.currentRotation = 0;
        this.promoForm.reset();
        this.promoWinner = null;
    }
}
